import React, { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { useTranslation } from '$hooks';
import { FlowStep, IBasketLine, ICalculatedGiftDetailsModel } from '$models';
import { formatPrice, formatString, mq } from '$lib/helpers';
import { Flex } from '$components/layouts';
import { ShippingMethod } from '$constants';
import { BasketItem } from '../basket-item';
import { GiftShop } from '~/modules/gift-shop/gift-shop';
import { formatISODateToString } from '$lib/helpers/date.helper';
import { Text } from '$components/elements/text';

type Props = {
    basketLines?: IBasketLine[];
    giftDetails?: ICalculatedGiftDetailsModel;
};

export const BasketGiftItems: FC<Props> = ({ basketLines, giftDetails }: Props): JSX.Element => {
    const { translate } = useTranslation();
    const [deliveryMessage, setDeliveryMessage] = useState('');
    const [isGiftShopModalOpen, setIsGiftShopModalOpen] = useState(false);
    const [giftFlowEditStep, setGiftFlowEditStep] = useState<FlowStep>();
    const theme = useTheme();

    const onEditInformation = () => {
        setGiftFlowEditStep(FlowStep.Delivery);
        setIsGiftShopModalOpen(true);
    };

    const onEditCardClicked = () => {
        setGiftFlowEditStep(FlowStep.Cards);
        setIsGiftShopModalOpen(true);
    };

    useEffect(() => {
        if (giftDetails?.shippingType) {
            switch (giftDetails?.shippingType?.shippingMethod) {
                case ShippingMethod.GiftShopSameDay:
                case ShippingMethod.GiftShopNextDay:
                    setDeliveryMessage(
                        formatString(
                            translate('checkout.giftDetails.giftDeliveryMessage'),
                            formatISODateToString(giftDetails?.deliveryDate),
                            giftDetails?.deliveryAddress?.name || '',
                            `${giftDetails?.deliveryAddress?.streetAndNumber} ${giftDetails?.deliveryAddress?.zip} - ${giftDetails?.deliveryAddress?.city}`
                        )
                    );

                    break;
                case ShippingMethod.GiftShopPickup:
                    setDeliveryMessage(
                        formatString(
                            translate('checkout.giftDetails.giftPickupMessage'),
                            formatISODateToString(giftDetails?.pickupDate),
                            giftDetails?.deliveryStoreInformation?.customerName || ''
                        )
                    );
                    break;
            }
        } else {
            setDeliveryMessage(translate('checkout.giftDetails.editGiftDeliveryText'));
        }
    }, [giftDetails]);

    return (
        <>
            <BasketGiftSectionWrapper justifyContent="space-between" column>
                <GiftSectionHeader column>
                    <Flex alignItems="center" gap="medium">
                        <img src="/assets/burd-logo.png" alt="Burd logo" height={50} width={100} />
                        <Flex column alignItems="flex-start" gap="extrasmall">
                            <Text lineHeight="1.3" size={theme.fontSizes.xs} fontWeight="Bold">
                                {deliveryMessage}
                            </Text>
                            <EditInfoButton onClick={() => onEditInformation()}>
                                {translate('checkout.giftDetails.editGiftInformation')}
                            </EditInfoButton>
                        </Flex>
                    </Flex>
                </GiftSectionHeader>
                <Flex column spacingBottom="small">
                    {basketLines?.map((line) => (
                        <BasketItem
                            key={line.product?.itemNumber}
                            line={line}
                            shippingType={undefined}
                            onEditCardClicked={() => onEditCardClicked()}
                        />
                    ))}
                </Flex>
                {giftDetails && (
                    <Flex justifyContent="space-between">
                        <Text fontWeight="Bold">{translate('checkout.giftDetails.giftShopDeliveryTitleLabel')}</Text>
                        <Text>{formatPrice(giftDetails?.shippingType?.calculatedShippingPrice)}</Text>
                    </Flex>
                )}
            </BasketGiftSectionWrapper>
            <GiftShop open={isGiftShopModalOpen} editStep={giftFlowEditStep} setOpen={setIsGiftShopModalOpen} />
        </>
    );
};

const BasketGiftSectionWrapper = styled(Flex)(({ theme }) => ({
    borderTop: `3px solid ${theme.colors.gift}`,
    backgroundColor: theme.colors.grey5,
    padding: theme.space[5],
    marginTop: theme.space[5],
    marginBottom: theme.space[5],
}));

const GiftSectionHeader = styled(Flex)(({ theme }) => ({
    paddingBottom: theme.space[5],
    marginBottom: theme.space[3],

    [mq('lg')]: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
    },
}));

const EditInfoButton = styled.button(({ theme }) => ({
    outline: 'none',
    border: 'none',
    color: theme.colors.red,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    padding: 0,
    lineHeight: 1.3,
    fontSize: theme.fontSizes.xs,
}));
